// AIドキュメント取り込み — Word/Excel/PDF → AI翻訳・整形 → ナレッジ記事(下書き)
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkle, UploadSimple, FileDoc, CircleNotch, CheckCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/knowledge/ai-doc-import`;
const ACCEPT = '.docx,.xlsx,.pdf,.txt,.md,.csv';

export default function AiDocImportDialog({ open, onClose, parentId }) {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [lang, setLang] = useState('ja');
  const [job, setJob] = useState(null); // {id, status, progress, article_slug, error}
  const [starting, setStarting] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const reset = () => { setFile(null); setLang('ja'); setJob(null); setStarting(false); };

  useEffect(() => {
    if (!job?.id || job.status === 'done' || job.status === 'error') return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/${job.id}`);
        setJob((prev) => ({ ...prev, ...data }));
        if (data.status === 'done') toast.success('記事を作成しました（下書き）');
        if (data.status === 'error') toast.error(data.error || 'AI処理に失敗しました');
      } catch (_e) { /* keep polling */ }
    }, 2500);
    return () => clearInterval(pollRef.current);
  }, [job?.id, job?.status]);

  const start = async () => {
    if (!file) { toast.error('ファイルをアップロードしてください'); return; }
    setStarting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('target_lang', lang);
      if (parentId) fd.append('parent_id', parentId);
      const { data } = await axios.post(API, fd);
      setJob({ id: data.job_id, status: 'processing', progress: '開始しています…' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || '開始に失敗しました');
    } finally { setStarting(false); }
  };

  const running = job && job.status === 'processing';
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !running) { reset(); onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid="kb-ai-doc-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-teal-700 text-white flex items-center justify-center">
              <Sparkle size={15} weight="fill" />
            </span>
            AIドキュメント取り込み
          </DialogTitle>
          <DialogDescription>
            Word / Excel / PDF / テキストをアップロードすると、AIが翻訳・整形してナレッジ記事（下書き）を作成します。
          </DialogDescription>
        </DialogHeader>

        {!job && (
          <div className="space-y-4">
            <button type="button" data-testid="kb-ai-doc-upload-zone"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
              className="w-full rounded-xl border-2 border-dashed border-zinc-300 dark:border-zinc-600 hover:border-blue-300 hover:bg-blue-50/40 p-6 text-center transition-colors">
              <UploadSimple size={24} className="mx-auto text-blue-600" weight="bold" />
              <div className="mt-2 text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                ファイルをアップロード（Word / Excel / PDF / テキスト）
              </div>
              <div className="mt-1 text-[11px] text-zinc-500">.docx / .xlsx / .pdf / .txt · 最大20MB</div>
              {file && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 text-xs font-semibold" data-testid="kb-ai-doc-file-chip">
                  <FileDoc size={14} weight="fill" />{file.name}
                </div>
              )}
            </button>
            <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" data-testid="kb-ai-doc-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <div>
              <Label className="text-xs font-semibold text-zinc-600 dark:text-zinc-300">出力言語</Label>
              <Select value={lang} onValueChange={setLang}>
                <SelectTrigger className="h-10 mt-1" data-testid="kb-ai-doc-lang"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ja">日本語へ翻訳して整形</SelectItem>
                  <SelectItem value="en">英語へ翻訳して整形</SelectItem>
                  <SelectItem value="none">翻訳せず整形のみ</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {job && (
          <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-4 text-center space-y-3" data-testid="kb-ai-doc-progress">
            {job.status === 'processing' && (
              <>
                <CircleNotch size={26} className="mx-auto text-blue-600 animate-spin" />
                <div className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">{job.progress || '処理中…'}</div>
                <div className="text-[11px] text-zinc-500">大きなドキュメントは数分かかることがあります。このままお待ちください。</div>
              </>
            )}
            {job.status === 'done' && (
              <>
                <CheckCircle size={28} weight="fill" className="mx-auto text-emerald-500" />
                <div className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">「{job.title}」を下書きとして作成しました</div>
                <Button className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="kb-ai-doc-open-article"
                  onClick={() => { const slug = job.article_slug || job.article_id; reset(); onClose(); navigate(`/knowledge/p/${slug}/edit`); }}>
                  記事を開いて編集
                </Button>
              </>
            )}
            {job.status === 'error' && (
              <div className="text-sm text-rose-600" data-testid="kb-ai-doc-error">{job.error || 'AI処理に失敗しました'}</div>
            )}
          </div>
        )}

        <DialogFooter>
          {!job && (
            <>
              <Button variant="outline" onClick={() => { reset(); onClose(); }} data-testid="kb-ai-doc-cancel">キャンセル</Button>
              <Button onClick={start} disabled={starting || !file} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="kb-ai-doc-start">
                {starting ? '開始中...' : 'AIで取り込む →'}
              </Button>
            </>
          )}
          {job && job.status !== 'processing' && (
            <Button variant="outline" onClick={() => { reset(); onClose(); }} data-testid="kb-ai-doc-close">閉じる</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
